import type { MapRef } from "react-map-gl/mapbox";

import type { GeoJSONMultiPolygon } from "api/model";
import { fromBoundaryMultiPolygon } from "context/PlotMap/boundaryUtils";
import { getBoundsFromCoordinates, type Bounds } from "context/PlotMap/geo";

export const FIT_BOUNDS_OPTIONS = { duration: 300, padding: 50 };

export const LOCATION_ZOOM = 14;

export const fitMapToBoundary = (
  map: MapRef | null | undefined,
  boundary: GeoJSONMultiPolygon | null | undefined,
): void => {
  const bounds = getBoundaryBounds(boundary);
  if (map == null || bounds == null) {
    return;
  }

  map.fitBounds(bounds, FIT_BOUNDS_OPTIONS);
};

export const fitMapToBoundaries = (
  map: MapRef | null | undefined,
  boundaries: Array<GeoJSONMultiPolygon | null | undefined>,
): void => {
  const bounds = getBoundariesBounds(boundaries);
  if (map == null || bounds == null) {
    return;
  }

  map.fitBounds(bounds, FIT_BOUNDS_OPTIONS);
};

export const getBoundaryBounds = (
  boundary: GeoJSONMultiPolygon | null | undefined,
): Bounds | undefined => {
  if (boundary == null) {
    return undefined;
  }

  const points = fromBoundaryMultiPolygon(boundary);
  if (points == null) {
    return undefined;
  }

  return getBoundsFromCoordinates(points);
};

export const getBoundariesBounds = (
  boundaries: Array<GeoJSONMultiPolygon | null | undefined>,
): Bounds | undefined => {
  const points = boundaries.flatMap((boundary) => {
    if (boundary == null) {
      return [];
    }

    return fromBoundaryMultiPolygon(boundary) ?? [];
  });

  return getBoundsFromCoordinates(points);
};

interface FitMapToLocationViewArgs {
  boundaryBounds: Bounds | undefined;
  hasLatLng: boolean;
  lat: number | null;
  lng: number | null;
  map: MapRef | null | undefined;
}

/** Fit/ease the map to the location boundary, or fallback to lcation lat/lng. */
export const fitMapToLocationView = ({
  boundaryBounds,
  hasLatLng,
  lat,
  lng,
  map,
}: FitMapToLocationViewArgs): boolean => {
  if (map == null) {
    return false;
  }

  if (boundaryBounds != null) {
    map.fitBounds(boundaryBounds, FIT_BOUNDS_OPTIONS);
    return true;
  }

  if (hasLatLng && lat != null && lng != null) {
    map.easeTo({
      center: [lng, lat],
      duration: FIT_BOUNDS_OPTIONS.duration,
      zoom: LOCATION_ZOOM,
    });
    return true;
  }

  return false;
};

export const toFlightPathGeoJson = (
  coordinates: Array<{ latitude: number; longitude: number; name: string }>,
) => ({
  type: "FeatureCollection" as const,
  features: coordinates.map((coordinate) => ({
    type: "Feature" as const,
    geometry: {
      type: "Point" as const,
      coordinates: [coordinate.longitude, coordinate.latitude],
    },
    properties: {
      name: coordinate.name,
    },
  })),
});
